import type { LocalizedDict } from '../index'

type TourStepCopy = {
  title: string
  body: string
}

export type SellerTourStrings = {
  stageLabel: string
  stepCounter: (current: number, total: number) => string
  back: string
  next: string
  last: string
  skip: string
  open: string
  close: string
  steps: {
    overview: TourStepCopy
    storesNav: TourStepCopy
    storesPage: TourStepCopy
    connectStore: TourStepCopy
    connectForm: TourStepCopy
    connectedStores: TourStepCopy
  }
  fallbacks: {
    targetMissingTitle: string
    targetMissingBody: string
    openStores: string
    storesUnavailableTitle: string
    storesUnavailableBody: string
    identityUnavailable: string
  }
  replay: {
    title: string
    description: string
    action: string
    unavailable: string
  }
}

export const sellerOnboardingTourI18n: LocalizedDict<SellerTourStrings> = {
  en: {
    stageLabel: 'Store setup',
    stepCounter: (current, total) => `${current} / ${total}`,
    back: 'Back',
    next: 'Continue',
    last: 'Finish',
    skip: 'Skip',
    open: 'Open guidance',
    close: 'Close',
    steps: {
      overview: {
        title: 'Welcome to Pexify',
        body: 'Let’s connect your first sales channel so orders can flow into one workspace.',
      },
      storesNav: {
        title: 'Your stores live here',
        body: 'Open Stores to connect Etsy or Shopify and monitor channel health.',
      },
      storesPage: {
        title: 'Manage every channel in one place',
        body: 'Connected stores, authorization state, and order intake controls stay together.',
      },
      connectStore: {
        title: 'Choose your first channel',
        body: 'Select Etsy or Shopify. Pexify will ask only for the details needed to begin authorization.',
      },
      connectForm: {
        title: 'Complete the connection details',
        body: 'Enter the requested shop information, then continue to the provider’s secure authorization screen.',
      },
      connectedStores: {
        title: 'Your stores are ready to manage',
        body: 'Review connection health and add another channel here whenever you need one.',
      },
    },
    fallbacks: {
      targetMissingTitle: 'This area is still loading',
      targetMissingBody: 'Open Stores directly and continue the setup from there.',
      openStores: 'Open Stores',
      storesUnavailableTitle: 'Stores are unavailable right now',
      storesUnavailableBody: 'Nothing was changed. Try again later from Resources.',
      identityUnavailable: 'We could not verify the seller account for this tour.',
    },
    replay: {
      title: 'Seller store setup tour',
      description: 'Replay the guided path from Seller Home to your Etsy or Shopify connection.',
      action: 'Start seller tour',
      unavailable: 'Open this page from the Seller workspace to start the tour.',
    },
  },
  tr: {
    stageLabel: 'Mağaza kurulumu',
    stepCounter: (current, total) => `${current} / ${total}`,
    back: 'Geri',
    next: 'Devam et',
    last: 'Bitir',
    skip: 'Atla',
    open: 'Rehberi aç',
    close: 'Kapat',
    steps: {
      overview: {
        title: 'Pexify’a hoş geldin',
        body: 'Siparişlerin tek çalışma alanına akması için ilk satış kanalını bağlayalım.',
      },
      storesNav: {
        title: 'Mağazaların burada',
        body: 'Etsy veya Shopify’ı bağlamak ve kanal sağlığını izlemek için Mağazalar’ı aç.',
      },
      storesPage: {
        title: 'Tüm kanalları tek yerde yönet',
        body: 'Bağlı mağazalar, yetkilendirme durumu ve sipariş alımı kontrolleri birlikte kalır.',
      },
      connectStore: {
        title: 'İlk kanalını seç',
        body: 'Etsy veya Shopify’ı seç. Pexify yalnızca yetkilendirme için gerekli bilgileri ister.',
      },
      connectForm: {
        title: 'Bağlantı bilgilerini tamamla',
        body: 'İstenen mağaza bilgilerini gir, ardından sağlayıcının güvenli yetkilendirme ekranına devam et.',
      },
      connectedStores: {
        title: 'Mağazaların yönetilmeye hazır',
        body: 'Bağlantı sağlığını buradan izle ve gerektiğinde yeni bir kanal ekle.',
      },
    },
    fallbacks: {
      targetMissingTitle: 'Bu alan hâlâ yükleniyor',
      targetMissingBody: 'Mağazalar’ı doğrudan açıp kuruluma oradan devam et.',
      openStores: 'Mağazalar’ı aç',
      storesUnavailableTitle: 'Mağazalar şu anda kullanılamıyor',
      storesUnavailableBody: 'Hiçbir şey değişmedi. Daha sonra Kaynaklar’dan yeniden dene.',
      identityUnavailable: 'Bu tur için seller hesabını doğrulayamadık.',
    },
    replay: {
      title: 'Seller mağaza kurulum turu',
      description: 'Seller Ana Sayfa’dan Etsy veya Shopify bağlantına uzanan rehberi yeniden oynat.',
      action: 'Seller turunu başlat',
      unavailable: 'Turu başlatmak için bu sayfayı Seller çalışma alanından aç.',
    },
  },
}
